import { getNoteChromaticIndex } from '@tunasong/music-lib'
import type { MelodyNote, NoteName } from '@tunasong/schemas'
import { chordIndexAt, judgeMelody, type MelodyHarmonyContext } from './judge-melody.js'

/**
 * The label an option ships with (melody toolkit M4; Bowie's rulings and Brian's call (a), 2026-10-29).
 *
 * - The model writes the label about its own notes. When repair changed a pitch the label may describe notes that are
 *   gone, so it is dropped and the client shows "Option n of total" alone. A respelling or a whole-option octave shift
 *   is not a change.
 * - A label is dropped when it ranks the option or claims what only the theory cues may say: AI output is never
 *   presented as the right answer (spec §6), and role words belong to the classifier.
 * - A numeric claim ships only when the notes bear it out, because a false "3rd" teaches wrong theory. "Lands on
 *   [note,] the Nth of X" is checked against the M3 classifier's degree of the last note over the last chord;
 *   "leaps a Nth" against the intervals between neighbouring notes. Any numeric claim it can't check drops the label.
 *
 * Deterministic labels built from the repaired notes are a follow-up, pending the user's copy decision.
 */

const forbidden = [
  /\bcorrect(ly)?\b/i,
  /\bbest\b/i,
  /\bright\b/i,
  /\bwrong\b/i,
  /\bsafe(ly)?\b/i,
  /\bfits? perfectly\b/i,
  /\bin key\b/i,
  /\bavoid notes?\b/i,
  /\boutside\b/i,
  /\bblue notes?\b/i,
  /\bchromatic(ally)?\b/i,
  /\bresolv(e|es|ed|ing)\b/i,
  /\b(sad|happy|hopeful|melancholy|melancholic|joyful|gloomy)\b/i,
]

const ordinalWords: Record<string, number> = {
  root: 1,
  second: 2,
  third: 3,
  fourth: 4,
  fifth: 5,
  sixth: 6,
  seventh: 7,
  octave: 8,
  ninth: 9,
  eleventh: 11,
  thirteenth: 13,
}

const ordinal = String.raw`\d+(?:st|nd|rd|th)|root|second|third|fourth|fifth|sixth|seventh|octave|ninth|eleventh|thirteenth`
const noteName = String.raw`[A-G](?:#|b|♯|♭)?`

/** Any numeric claim; each must be inside a claim that checked out */
const numericToken = new RegExp(String.raw`(?:^|[^\w])(${ordinal})(?![\w])`, 'i')
const landsClaim = new RegExp(
  String.raw`[Ll]ands? on (?:(${noteName})(?![\w#♯♭]),? ?)?(?:(?:the |a )?(${ordinal})(?: of (${noteName})[^\s.,;]*)?)?`,
  'g'
)
const leapClaim = new RegExp(
  String.raw`\b(?:leaps?|jumps?|rises?|falls?|drops?|climbs?|skips?)(?: up| down)?(?: by)? (?:a|an) (${ordinal})(?![\w-])`,
  'gi'
)

/** The semitones each interval name may mean (Bowie): 4th and 5th are perfect only */
const intervalSemitones: Record<number, number[]> = {
  2: [1, 2],
  3: [3, 4],
  4: [5],
  5: [7],
  6: [8, 9],
  7: [10, 11],
  8: [12],
}

const numberOf = (claim: string) => ordinalWords[claim.toLowerCase()] ?? Number.parseInt(claim, 10)

const pitchClassOf = (name: string) => getNoteChromaticIndex(name.replace('♯', '#').replace('♭', 'b') as NoteName)

const mod12 = (value: number) => ((value % 12) + 12) % 12

export const melodyOptionLabel = (
  label: string,
  repair: { notes: MelodyNote[]; changed: number[] },
  context: MelodyHarmonyContext
): string => {
  const line = (label.split('\n')[0] ?? '').trim()
  if (repair.changed.length > 0 || forbidden.some(pattern => pattern.test(line))) {
    return ''
  }

  const judged = judgeMelody(repair.notes, context)
  const last = judged.at(-1)
  const lastNote = repair.notes.at(-1)
  const lastChord = lastNote ? context.chords[chordIndexAt(context.chords, lastNote.position)] : undefined
  const intervals = judged.slice(1).map((note, index) => Math.abs(note.midi - (judged[index]?.midi ?? note.midi)))

  let unchecked = line
  for (const match of line.matchAll(landsClaim)) {
    const [whole, name, degree, chordRoot] = match
    if (!name && !degree) {
      continue
    }
    const isNameTrue = !name || (last !== undefined && pitchClassOf(name) === mod12(last.midi))
    const degreeNumber = last?.role?.degreeNumber.replace(/\D/g, '')
    const isDegreeTrue = !degree || (degreeNumber !== undefined && Number(degreeNumber) === numberOf(degree))
    const isChordTrue =
      !chordRoot || (lastChord !== undefined && pitchClassOf(chordRoot) === pitchClassOf(lastChord.root))
    if (!isNameTrue || !isDegreeTrue || !isChordTrue) {
      return ''
    }
    unchecked = unchecked.replace(whole, ' ')
  }
  for (const match of line.matchAll(leapClaim)) {
    const [whole, size] = match
    const semitones = size ? intervalSemitones[numberOf(size)] : undefined
    if (!semitones || !intervals.some(interval => semitones.includes(interval))) {
      return ''
    }
    unchecked = unchecked.replace(whole, ' ')
  }

  return numericToken.test(unchecked) ? '' : line
}
